/**
 * Global Users Management Page (Super Admin Only)
 *
 * Allows super admins to:
 * - Create users and assign to multiple tenants
 * - Manage user-tenant-role relationships
 * - Create roles on the fly
 */

import { useState } from 'react';
import { Table, Button, Space, Tag, Modal, Form, Input, Select, message } from 'antd';
import { PlusOutlined, UserOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@api/client';

interface GlobalUser {
  id: string;
  email: string;
  full_name: string;
  is_super_admin: boolean;
  is_active: boolean;
  tenant_memberships: {
    tenant_id: string;
    tenant_name: string;
    role_id: string;
    role_name: string;
  }[];
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
}

interface Role {
  id: string;
  name: string;
  tenant_id: string;
}

export default function GlobalUsersPage() {
  const queryClient = useQueryClient();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [roleForm] = Form.useForm();

  // Fetch all users
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const response = await apiClient.get('/v1/tenants/all-users');
      return response.data as GlobalUser[];
    },
  });

  // Fetch all tenants
  const { data: tenants = [] } = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: async () => {
      const response = await apiClient.get('/v1/tenants');
      return response.data.items as Tenant[];
    },
  });

  // Roles are fetched per-tenant in the TenantRoleRow component
  // (removed unused roles query here to avoid errors)

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: async (data: {
      email: string;
      full_name: string;
      password: string;
      is_super_admin: boolean;
      tenant_assignments: { tenant_id: string; role_id: string }[];
    }) => {
      const response = await apiClient.post('/v1/user-management/global-user', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      message.success('User created successfully');
      setCreateModalOpen(false);
      form.resetFields();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to create user');
    },
  });

  // Create role mutation
  const createRoleMutation = useMutation({
    mutationFn: async (data: { tenant_id: string; name: string; permissions: string[] }) => {
      const response = await apiClient.post(`/v1/tenants/${data.tenant_id}/roles`, {
        name: data.name,
        permissions: data.permissions,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      message.success('Role created successfully');
      setRoleModalOpen(false);
      roleForm.resetFields();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to create role');
    },
  });

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Name',
      dataIndex: 'full_name',
      key: 'full_name',
    },
    {
      title: 'Type',
      key: 'type',
      render: (_: any, record: GlobalUser) => (
        <Tag color={record.is_super_admin ? 'red' : 'blue'}>
          {record.is_super_admin ? 'Super Admin' : 'User'}
        </Tag>
      ),
    },
    {
      title: 'Tenant Memberships',
      key: 'memberships',
      render: (_: any, record: GlobalUser) => (
        <Space wrap>
          {record.tenant_memberships.map((m) => (
            <Tag key={m.tenant_id}>
              {m.tenant_name} - {m.role_name}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: GlobalUser) => (
        <Tag color={record.is_active ? 'green' : 'red'}>
          {record.is_active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
  ];

  const handleCreateUser = async () => {
    try {
      const values = await form.validateFields();
      createUserMutation.mutate(values);
    } catch (error) {
      // Validation failed
    }
  };

  const handleCreateRole = async () => {
    try {
      const values = await roleForm.validateFields();
      createRoleMutation.mutate(values);
    } catch (error) {
      // Validation failed
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Global User Management</h1>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalOpen(true)}
          >
            Create User
          </Button>
          <Button
            icon={<UserOutlined />}
            onClick={() => setRoleModalOpen(true)}
          >
            Create Role
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={isLoading}
      />

      {/* Create User Modal */}
      <Modal
        title="Create New User"
        open={createModalOpen}
        onOk={handleCreateUser}
        onCancel={() => {
          setCreateModalOpen(false);
          form.resetFields();
        }}
        confirmLoading={createUserMutation.isPending}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Invalid email' },
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>

          <Form.Item
            name="full_name"
            label="Full Name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="John Doe" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Password is required' },
              { min: 8, message: 'Password must be at least 8 characters' },
            ]}
          >
            <Input.Password placeholder="Minimum 8 characters" />
          </Form.Item>

          <Form.Item
            name="is_super_admin"
            label="Super Admin"
            valuePropName="checked"
            initialValue={false}
          >
            <Select>
              <Select.Option value={false}>No</Select.Option>
              <Select.Option value={true}>Yes</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="tenant_assignments"
            label="Tenant Assignments"
            rules={[{ required: true, message: 'At least one tenant assignment is required' }]}
          >
            <TenantRoleAssignments tenants={tenants} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Create Role Modal */}
      <Modal
        title="Create New Role"
        open={roleModalOpen}
        onOk={handleCreateRole}
        onCancel={() => {
          setRoleModalOpen(false);
          roleForm.resetFields();
        }}
        confirmLoading={createRoleMutation.isPending}
      >
        <Form form={roleForm} layout="vertical">
          <Form.Item
            name="tenant_id"
            label="Tenant"
            rules={[{ required: true, message: 'Tenant is required' }]}
          >
            <Select placeholder="Select tenant">
              {tenants.map((t) => (
                <Select.Option key={t.id} value={t.id}>
                  {t.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="name"
            label="Role Name"
            rules={[{ required: true, message: 'Role name is required' }]}
          >
            <Input placeholder="e.g., Manager, Viewer" />
          </Form.Item>

          <Form.Item
            name="permissions"
            label="Permissions"
            rules={[{ required: true, message: 'At least one permission is required' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select permissions"
              options={[
                { value: '*', label: 'All Permissions (Admin)' },
                { value: 'templates:read', label: 'View Templates' },
                { value: 'templates:write', label: 'Create/Edit Templates' },
                { value: 'instances:read', label: 'View Instances' },
                { value: 'instances:write', label: 'Create/Edit Instances' },
                { value: 'users:read', label: 'View Users' },
                { value: 'users:write', label: 'Manage Users' },
                { value: 'tenant:manage', label: 'Manage Tenant Settings' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// Component for managing tenant-role assignments
function TenantRoleAssignments({ tenants, value = [], onChange }: any) {
  const [assignments, setAssignments] = useState<{ tenant_id: string; role_id: string }[]>(value);
  const [selectedTenants, setSelectedTenants] = useState<Set<string>>(new Set(value.map((a: any) => a.tenant_id)));

  const addAssignment = () => {
    const newAssignments = [...assignments, { tenant_id: '', role_id: '' }];
    setAssignments(newAssignments);
    onChange?.(newAssignments);
  };

  const updateAssignment = (index: number, field: 'tenant_id' | 'role_id', value: string) => {
    const newAssignments = [...assignments];
    newAssignments[index] = { ...newAssignments[index], [field]: value };

    if (field === 'tenant_id') {
      const newSelected = new Set(selectedTenants);
      if (value) {
        newSelected.add(value);
      }
      setSelectedTenants(newSelected);
    }

    setAssignments(newAssignments);
    onChange?.(newAssignments);
  };

  const removeAssignment = (index: number) => {
    const newAssignments = assignments.filter((_, i) => i !== index);
    const newSelected = new Set(newAssignments.map(a => a.tenant_id));
    setSelectedTenants(newSelected);
    setAssignments(newAssignments);
    onChange?.(newAssignments);
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {assignments.map((assignment, index) => (
        <TenantRoleRow
          key={index}
          assignment={assignment}
          tenants={tenants}
          selectedTenants={selectedTenants}
          onUpdate={(field, value) => updateAssignment(index, field, value)}
          onRemove={() => removeAssignment(index)}
        />
      ))}
      <Button type="dashed" onClick={addAssignment} block icon={<PlusOutlined />}>
        Add Tenant Assignment
      </Button>
    </Space>
  );
}

interface TenantRoleRowProps {
  assignment: { tenant_id: string; role_id: string };
  tenants: Tenant[];
  selectedTenants: Set<string>;
  onUpdate: (field: 'tenant_id' | 'role_id', value: string) => void;
  onRemove: () => void;
}

function TenantRoleRow({ assignment, tenants, selectedTenants, onUpdate, onRemove }: TenantRoleRowProps) {
  const { data: roles = [] } = useQuery({
    queryKey: ['roles', assignment.tenant_id],
    queryFn: async () => {
      if (!assignment.tenant_id) return [];
      const response = await apiClient.get(`/v1/tenants/${assignment.tenant_id}/roles`);
      return response.data as Role[];
    },
    enabled: !!assignment.tenant_id,
  });

  return (
    <Space style={{ width: '100%' }}>
      <Select
        style={{ width: 200 }}
        placeholder="Select tenant"
        value={assignment.tenant_id || undefined}
        onChange={(value) => onUpdate('tenant_id', value)}
      >
        {tenants.map((t: Tenant) => (
          <Select.Option
            key={t.id}
            value={t.id}
            disabled={selectedTenants.has(t.id) && t.id !== assignment.tenant_id}
          >
            {t.name}
          </Select.Option>
        ))}
      </Select>

      <Select
        style={{ width: 200 }}
        placeholder="Select role"
        value={assignment.role_id || undefined}
        onChange={(value) => onUpdate('role_id', value)}
        disabled={!assignment.tenant_id}
        loading={!roles.length && !!assignment.tenant_id}
      >
        {roles.map((r: Role) => (
          <Select.Option key={r.id} value={r.id}>
            {r.name}
          </Select.Option>
        ))}
      </Select>

      <Button
        type="text"
        danger
        icon={<DeleteOutlined />}
        onClick={onRemove}
      />
    </Space>
  );
}
